import { z } from "zod";
import { NotificationType } from "../../entities/notification";
import { formatReportDate, stringToDate } from "../../utils/date";
export const notificationLineItemSchema = z.object({
    contractorPackageId: z.string(),
    created: z.date(),
    deleted: z.boolean().optional(),
    id: z.string(),
    /**
     * Label to render wherever the organisation is named. Canonical form of
     * `organisationName` with shouted casing normalised, falling back to it.
     * Keep using the raw name on contracts, reports and exports.
     */
    organisationDisplayName: z.string(),
    organisationId: z.string(),
    organisationLogo: z.string(),
    organisationName: z.string(),
    projectId: z.string(),
    projectName: z.string(),
    reportId: z.string().optional(),
    type: z.enum(NotificationType),
    // User-specific view status (only present when using WithUserStatus functions)
    userViewed: z.boolean().optional(),
    viewedAt: z.date().optional()
});
export const notificationTypeToTitle = (notificationType) => {
    switch (notificationType) {
        case NotificationType.PROJECT_REPORT_SUBMITTED:
            return "Project Report Shared";
        case NotificationType.MONTHLY_REPORT_SUBMITTED:
            return "Monthly Report Submitted";
        case NotificationType.MONTHLY_REPORT_REJECTED:
            return "Monthly Report Rejected";
        case NotificationType.MONTHLY_REPORT_ACCEPTED:
            return "Monthly Report Accepted";
        case NotificationType.SUBCONTRACTOR_ACCEPTED:
            return "Subcontractor Accepted";
        case NotificationType.PRODUCT_UPDATES:
            return "Product Updates";
    }
};
/**
 * What the type means, for the Settings toggle list — the body text helpers below all
 * need a live notification to interpolate, so they cannot describe a type in the
 * abstract. Phrased around who does what, since that is what someone deciding whether
 * to switch it off actually wants to know.
 */
export const notificationTypeToDescription = (notificationType) => {
    switch (notificationType) {
        case NotificationType.PROJECT_REPORT_SUBMITTED:
            return "When another organisation shares a project report with you.";
        case NotificationType.MONTHLY_REPORT_SUBMITTED:
            return "When a subcontractor submits their monthly report to you.";
        case NotificationType.MONTHLY_REPORT_REJECTED:
            return "When a monthly report you submitted is sent back for changes.";
        case NotificationType.MONTHLY_REPORT_ACCEPTED:
            return "When a monthly report you submitted is approved.";
        case NotificationType.SUBCONTRACTOR_ACCEPTED:
            return "When a subcontractor accepts an invite to one of your projects.";
        case NotificationType.PRODUCT_UPDATES:
            return "Occasional emails about new Catchment features and improvements.";
    }
};
export const notificationTypeToBodyText = (notification) => {
    const reportDate = notification.reportId
        ? stringToDate(notification.reportId)
        : null;
    switch (notification.type) {
        case NotificationType.PROJECT_REPORT_SUBMITTED:
            return `${notification.organisationName} has submitted the ${reportDate ? formatReportDate(reportDate) : ""} Project Report for ${notification.projectName}`;
        case NotificationType.MONTHLY_REPORT_SUBMITTED:
            return `${notification.organisationName} has submitted the ${reportDate ? formatReportDate(reportDate) : ""} Monthly Report for ${notification.projectName}`;
        case NotificationType.MONTHLY_REPORT_REJECTED:
            return `Your ${reportDate ? formatReportDate(reportDate) : ""} Monthly Report has been rejected`;
        case NotificationType.MONTHLY_REPORT_ACCEPTED:
            return `Your ${reportDate ? formatReportDate(reportDate) : ""} Monthly Report has been accepted`;
        case NotificationType.SUBCONTRACTOR_ACCEPTED:
            return `${notification.organisationName} has accepted their invite to collaborate on ${notification.projectName}`;
        // Email-only type — no notification row is ever written with it, so the
        // drawer never reaches this branch. It returns real copy rather than an
        // empty string so the "every type has body text" invariant still holds.
        case NotificationType.PRODUCT_UPDATES:
            return "Catchment product update";
    }
};
